import React, { Component } from "react";
import { Tabs, Alert } from "antd";
import { connect } from "react-redux";
import {
  fetchOnHoldOrders,
  fetchPendingOrders,
  fetchProcessingOrders,
  fetchBlankSelectionOrders,
  setActiveTabKey,
  fetchStockOrdersList,
  getStockPickingOrder,
  getMultiStockGroupedOrders,
} from "../homeActions";
import OnHoldTab from "./tabPanes/OnHoldTab";
import PendingAuthTab from "./tabPanes/PendingAuthTab";
import UnderProcessingTab from "./tabPanes/UnderProcessingTab";
import BlankSelectionTab from "./tabPanes/BlankSelectionTab";
import { PermissibleRender } from "@brainhubeu/react-permissible";
import { getUserPermissions } from "../../../app/common/util/genericUtils";
import StockOrdersTab from "./tabPanes/StockOrdersTab";
import StockPickingTab from "./tabPanes/StockPickingTab";
import MultiStockGroupedTab from "./tabPanes/MultiStockGroupedTab";
import { getIsMobileViewport } from "../../../app/common/util/useIsMobile";

const TabPane = Tabs.TabPane;

const MOBILE_HIDDEN_TAB_KEYS = ["4", "6", "7"];
const TAB_COUNT_BADGE_KEYS = ["1", "2", "3"];

const getTabOrderCount = (orders) => {
  if (!orders || Array.isArray(orders) || typeof orders !== "object") {
    return null;
  }
  if (orders.total_count != null && orders.total_count !== "") {
    return Number(orders.total_count);
  }
  if (orders.count != null && orders.count !== "") {
    return Number(orders.count);
  }
  return null;
};

const tabs = [
  {
    component: OnHoldTab,
    label: "On Hold",
    key: 1,
    slug: "on-hold",
    requiredPermissions: ["can_view_onhold_orders"],
  },
  {
    component: PendingAuthTab,
    label: "Authorization",
    key: 2,
    slug: "authorization",
    requiredPermissions: ["can_view_pendingauth_orders"],
  },
  {
    component: UnderProcessingTab,
    label: "Processing",
    key: 3,
    slug: "processing",
    requiredPermissions: ["can_view_processing_orders"],
  },
  {
    component: BlankSelectionTab,
    label: "Blank Selection",
    key: 4,
    slug: "blank-selection",
    requiredPermissions: ["can_do_blankselection"],
    hideOnMobile: true,
  },
  {
    component: StockOrdersTab,
    label: "Stock Orders",
    key: 5,
    slug: "stock-orders",
    requiredPermissions: ["can_view_stock_orders"],
  },
  {
    component: StockPickingTab,
    label: "Stock Picking (Single)",
    key: 6,
    slug: "stock-picking",
    requiredPermissions: ["can_pick_stock_order"],
    hideOnMobile: true,
  },
  {
    component: MultiStockGroupedTab,
    label: "Stock Picking (Multi)",
    key: 7,
    slug: "stock-picking-multi",
    requiredPermissions: ["can_pick_stock_order"],
    hideOnMobile: true,
  },
];

class TabView extends Component {
  state = {
    isMobile: getIsMobileViewport(),
  };

  componentDidMount() {
    this.handleResize = () => {
      const isMobile = getIsMobileViewport();
      this.setState({ isMobile }, () => {
        this.ensureValidActiveTab(this.props.activeKey);
      });
    };
    window.addEventListener("resize", this.handleResize);
    this.ensureValidActiveTab(this.props.activeKey);
    this.refreshOrders(this.getValidActiveKey(this.props.activeKey));
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  getValidActiveKey = (activeKey) => {
    const isMobile = getIsMobileViewport();
    if (isMobile && MOBILE_HIDDEN_TAB_KEYS.includes(String(activeKey))) {
      return "1";
    }
    return activeKey;
  };

  ensureValidActiveTab = (activeKey) => {
    const validKey = this.getValidActiveKey(activeKey);
    if (validKey !== String(activeKey)) {
      this.refreshOrders(validKey);
    }
  };

  getVisibleTabs = () => {
    if (!this.state.isMobile) return tabs;
    return tabs.filter((tab) => !tab.hideOnMobile);
  };

  refreshOrders = (activeKey) => {
    const {
      fetchOnHoldOrders,
      fetchPendingOrders,
      fetchProcessingOrders,
      fetchBlankSelectionOrders,
      setActiveTabKey,
      fetchStockOrdersList,
      getStockPickingOrder,
      getMultiStockGroupedOrders,
    } = this.props;
    setActiveTabKey(activeKey);
    const mapper = [
      { key: "1", func: fetchOnHoldOrders },
      { key: "2", func: fetchPendingOrders },
      { key: "3", func: fetchProcessingOrders },
      { key: "4", func: fetchBlankSelectionOrders },
      { key: "5", func: fetchStockOrdersList },
      { key: "6", func: getStockPickingOrder },
      { key: "7", func: getMultiStockGroupedOrders },
    ];
    mapper.forEach((item) => {
      item.key === activeKey && item.func();
    });
  };

  getOrdersForTab = (tabKey) => {
    const { onHoldOrders, pendingAuthOrders, processingOrders } = this.props;
    const ordersByTab = {
      1: onHoldOrders,
      2: pendingAuthOrders,
      3: processingOrders,
    };
    return ordersByTab[tabKey];
  };

  getActiveTabSlug = () => {
    const { activeKey } = this.props;
    const active = tabs.find((tab) => String(tab.key) === String(activeKey));
    return (active && active.slug) || "on-hold";
  };

  renderTabLabel = (tab) => {
    const { activeKey } = this.props;
    const tabKey = String(tab.key);
    const isCountTab = TAB_COUNT_BADGE_KEYS.includes(tabKey);
    const isActiveTab = tabKey === String(activeKey);
    const count =
      isCountTab && isActiveTab
        ? getTabOrderCount(this.getOrdersForTab(tab.key))
        : null;

    return (
      <span className={`tab-label-with-badge home-tab-pill home-tab-pill--${tab.slug}`}>
        <span className="home-tab-pill__dot" aria-hidden="true" />
        <span className="tab-label-text">{tab.label}</span>
        {count != null && !Number.isNaN(count) && (
          <span className="tab-count-badge" aria-label={`${count} orders`}>
            {count}
          </span>
        )}
      </span>
    );
  };

  render() {
    const { activeKey } = this.props;
    const userPermissions = getUserPermissions();
    const visibleTabs = this.getVisibleTabs();
    const activeSlug = this.getActiveTabSlug();

    return (
      <div className={`tab-container tab-container--${activeSlug}`}>
        <Tabs
          className="home-order-tabs"
          onChange={this.refreshOrders}
          activeKey={activeKey}
          animated={false}
        >
          {visibleTabs.map((tab) => {
            return (
              <TabPane tab={this.renderTabLabel(tab)} key={tab.key}>
                <div className="home-order-table-shell">
                  <PermissibleRender
                    userPermissions={userPermissions}
                    requiredPermissions={tab.requiredPermissions}
                    renderOtherwise={
                      <Alert message="Access Denied" type="error" />
                    }
                  >
                    <tab.component />
                  </PermissibleRender>
                </div>
              </TabPane>
            );
          })}
        </Tabs>
      </div>
    );
  }
}
const mapStateToProps = (state) => ({
  activeKey: state.home.activeTabKey,
  onHoldOrders: state.home.onHoldOrders,
  pendingAuthOrders: state.home.pendingAuthOrders,
  processingOrders: state.home.processingOrders,
});
const actions = {
  fetchOnHoldOrders,
  fetchPendingOrders,
  fetchProcessingOrders,
  fetchBlankSelectionOrders,
  setActiveTabKey,
  fetchStockOrdersList,
  getStockPickingOrder,
  getMultiStockGroupedOrders,
};
export default connect(
  mapStateToProps,
  actions
)(TabView);
